import { Color, getThemeColor } from "@mcleod/core";
import { Component } from "../..";
import { Panel } from "../components/panel/Panel";

/**
 * A SeparatorPanel is used to render a separator line without adding any height/width to the DOM.  The panel itself
 * is rendered no width/height (based on if the separator line is vertical/horizontal); the separator line is displayed
 * using pseudo-elements via a style tag.
 */
export class SeparatorPanel extends Panel {
    private static counter = 0;
    private styleElement: HTMLStyleElement;

    constructor(enclosingComponent: Component, vertical: boolean, lengthInPercent: number = 90,
        color: Color = "strokeSecondary") {
        super({
            id: SeparatorPanel.getId(enclosingComponent),
            margin: 0,
            padding: 0,
            rowBreak: !vertical,
            style: { position: "relative" },
        });
        if (vertical) {
            this.width = 0;
        } else {
            this.height = 0;
            this.width = "100%";
        }
        this.createStyleElement(vertical, lengthInPercent, color);
        this.addStyleElementToDOM();
    }

    private static getId(enclosingComponent: Component): string {
        const baseId = `separator${SeparatorPanel.getCounter()}`;
        return enclosingComponent != null ? enclosingComponent.createInnerComponentId(baseId) : baseId;
    }

    private static getCounter(): number {
        if (SeparatorPanel.counter >= 9999) {
            SeparatorPanel.counter = 0;
        }
        return ++SeparatorPanel.counter;
    }

    private createStyleElement(vertical: boolean, lengthInPercent: number, color: Color) {
        const offset = `${(100 - lengthInPercent)/2}%`;
        const resolvedColor = getThemeColor(color);
        let variableCSS: string;
        if (vertical) {
            variableCSS = `
                top: ${offset};
                bottom: ${offset};
                left: -0.5px;  /* shifts border to the center of the gap */
                border-left: 1px solid ${resolvedColor};
            `;
        } else {
            variableCSS = `
                left: ${offset};
                right: ${offset};
                top: -0.5px;  /* shifts border to the center of the gap */
                border-top: 1px solid ${resolvedColor};
            `;
        }
        // z-index below ensures that the separator line is not covered up by its neighboring list items
        const styleElementCSS = `
            #${this.id}::before {
                content: '';
                position: absolute;
                z-index: 1;
                ${variableCSS}
            }
        `;
        this.styleElement = document.createElement('style');
        this.styleElement.setAttribute('data-id', this.id);
        this.styleElement.textContent = styleElementCSS;
    }

    private addStyleElementToDOM() {
        this.addMountListener(() => {
            document.head.appendChild(this.styleElement);
        });
        this.addUnmountListener(() => {
            if (document.head.contains(this.styleElement)) {
                document.head.removeChild(this.styleElement);
            }
        });
    }
}
